import _ from 'lodash'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { createColumnHelper, RowData } from '@tanstack/react-table'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'

import { Chip, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Tooltip } from '@mui/material'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import LaunchIcon from '@mui/icons-material/Launch'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import { CompareArrows, Warning } from '@mui/icons-material'
import VideoCallIcon from '@mui/icons-material/VideoCall'
import RateReviewIcon from '@mui/icons-material/RateReview'
import AssignmentIcon from '@mui/icons-material/Assignment'
import DescriptionIcon from '@mui/icons-material/Description'

import OrderPaymentBadge from './OrderPaymentBadge'
import ImmediateConsultationBadge from './ImmediateConsultationBadge'
import ChipButton from '@/components/ui/buttons/ChipButton'
import HeaderConfiguration from '@/components/ui/tables/HeaderConfiguration'
import Table, { TableProps } from '@/components/ui/tables/Table'

import { OrderStatus } from '@/core/models/order.model'
import { AvailabilityMeetingPlace } from '@/core/models/meeting-link.model'
import { ConvertOrderDialogContainer } from '../containers/ConvertOrderDialog'

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    acceptOrder?: (orderId: string) => void
    cancelOrder?: (orderId: string) => void
    refuseOrder?: (orderId: string) => void
    startLocalConsultation?: (orderId: string, patientId: string) => void
  }
}

export type OrderData = {
  id: string
  cancelReason?: string
  consultationId?: string
  meetingPlace?: string
  meetingPlaceDescription: string
  patient: {
    id: string
    fullName: string
  } | null
  speciality: string
  startDate: string
  status: string
  filesCount?: number
  isPrioritized?: boolean
  comment?: string
  referenceId: string
  hasFormSubmission?: boolean
  payment?: any
  requiresPayment?: boolean
  paymentCompleted?: boolean
  createdAt?: string
}

export type OrdersTableProps = Pick<TableProps<OrderData>, 'data' | 'loading' | 'manualPagination' | 'onPaginationChange'> & {
  isDoctor?: boolean
  loading?: boolean
  onAcceptOrder?: (orderId: string) => void
  onCancelOrder?: (orderId: string) => void
  onPageChange?: (newPage: number, rowsPerPage: number) => void
  onRefuseOrder?: (orderId: string) => void
  onStartLocalConsultation?: (orderId: string, patientId: string) => void
  rowsPerPageOptions?: number[]
  totalCount: number
  onRowsPerPageChange?: (rowsPerPage: number) => void
}

const columnHelper = createColumnHelper<OrderData>()

export function OrdersTable(props: OrdersTableProps) {
  const { data, manualPagination, onAcceptOrder, onCancelOrder, onPaginationChange, onRefuseOrder, onStartLocalConsultation, totalCount } = props
  const { t } = useTranslation()
  const [selectedOrder, setSelectedOrder] = useState<string | null>(null)
  const [convertDialogOpen, setConvertDialogOpen] = useState(false)

  const handleConvertClick = (orderId: string) => {
    setSelectedOrder(orderId)
    setConvertDialogOpen(true)
  }

  const handleCloseConvertDialog = () => {
    setSelectedOrder(null)
    setConvertDialogOpen(false)
  }

  const columns = useMemo(
    () => [
      columnHelper.accessor('referenceId', {
        header: t('page.meetingLink.ordersTable.header.reference').toString(),
        cell: ({ getValue }) => (
          <Tooltip title={t('page.meetingLink.ordersTable.tooltip.reference')}>
            <span className="font-mono">{getValue()}</span>
          </Tooltip>
        ),
      }),
      columnHelper.accessor('patient.fullName', {
        header: t('page.meetingLink.ordersTable.header.patient').toString(),
        cell: ({ getValue, row }) => (
          <Stack direction="row" alignItems="center" spacing={1}>
            <span>{getValue()}</span>
            {row.original.isPrioritized && (
              <Tooltip title={t('page.meetingLink.ordersTable.tooltip.priority')}>
                <Warning fontSize="small" color="warning" />
              </Tooltip>
            )}
          </Stack>
        ),
      }),
      columnHelper.accessor('speciality', {
        header: t('page.meetingLink.ordersTable.header.speciality').toString(),
      }),
      columnHelper.accessor('startDate', {
        header: t('page.meetingLink.ordersTable.header.startDate').toString(),
        cell: ({ getValue, row }) => {
          const date = dayjs(getValue())
          return (
            <Stack direction="row" alignItems="center">
              <Stack>
                <span>{date.format('DD/MM/YYYY')}</span>
                <span className="text-gray-500 text-sm">{date.format('HH[h]mm')}</span>
              </Stack>
              <ImmediateConsultationBadge startDate={row.original.startDate} createdAt={row.original.createdAt} />
            </Stack>
          )
        },
      }),
      columnHelper.accessor('meetingPlace', {
        header: t('page.meetingLink.ordersTable.header.meetingPlace').toString(),
        cell: ({ getValue, row }) => {
          const placeId = getValue()
          const description = row.original.meetingPlaceDescription

          let icon = null
          let label = description

          if (placeId === AvailabilityMeetingPlace.VISIO_CONFERENCE) {
            icon = <VideoCallIcon className="text-blue-500" fontSize="small" />
            label = t('page.meetingLink.availabilitiesTable.cell.meetingPlace.visioConference')
          } else if (placeId === AvailabilityMeetingPlace.SECOND_OPINION) {
            icon = <RateReviewIcon className="text-purple-500" fontSize="small" />
            label = t('page.meetingLink.availabilitiesTable.cell.meetingPlace.secondOpinion')
          }

          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              {icon}
              <span>{label}</span>
            </Stack>
          )
        },
      }),
      columnHelper.accessor('filesCount', {
        header: t('page.meetingLink.ordersTable.header.files').toString(),
        cell: ({ getValue }) => {
          const count = getValue()
          return count ? (
            <Tooltip title={t('page.meetingLink.ordersTable.tooltip.files', { count })}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <DescriptionIcon fontSize="small" className="text-blue-500" />
                <span>{count}</span>
              </Stack>
            </Tooltip>
          ) : null
        },
      }),
      columnHelper.accessor('hasFormSubmission', {
        header: t('page.meetingLink.ordersTable.header.formSubmission').toString(),
        cell: ({ getValue }) => {
          const hasForm = getValue()
          return hasForm ? (
            <Tooltip title={t('page.meetingLink.ordersTable.tooltip.formSubmission')}>
              <AssignmentIcon fontSize="small" className="text-green-500" />
            </Tooltip>
          ) : null
        },
      }),
      columnHelper.accessor('comment', {
        header: t('page.meetingLink.ordersTable.header.comment').toString(),
        cell: ({ getValue }) => {
          const comment = getValue()
          return comment ? (
            <Tooltip title={comment}>
              <span className="line-clamp-2 max-w-[200px]">{comment}</span>
            </Tooltip>
          ) : null
        },
      }),
      columnHelper.display({
        id: 'payment',
        header: t('page.meetingLink.ordersTable.header.payment').toString(),
        cell: ({ row }) => (
          <OrderPaymentBadge
            payment={row.original.payment}
            requiresPayment={row.original.requiresPayment}
            paymentCompleted={row.original.paymentCompleted}
          />
        ),
      }),
      columnHelper.accessor('status', {
        header: t('page.meetingLink.ordersTable.header.status').toString(),
        cell: ({ getValue, row }) => (
          <Stack spacing={0.5}>
            <Chip label={t(`common:model.order.status.${_.camelCase(getValue())}.label`)} size="small" color={getStatusColor(getValue())} />
            {getValue() === OrderStatus.Canceled && row.original.cancelReason && (
              <Tooltip title={row.original.cancelReason}>
                <span className="text-xs text-gray-500 truncate max-w-[150px]">{row.original.cancelReason}</span>
              </Tooltip>
            )}
          </Stack>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: HeaderConfiguration,
        cell: ({ row, table }) => (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
            {/* Primary Actions */}
            {row.original.status === OrderStatus.Pending && (
              <>
                <Tooltip title={t('common:button.accept.tooltip')}>
                  <ChipButton color="success" onClick={() => table.options.meta?.acceptOrder?.(row.original.id)}>
                    <CheckRoundedIcon />
                  </ChipButton>
                </Tooltip>
                <Tooltip title={t('common:button.refuse.tooltip')}>
                  <ChipButton color="error" onClick={() => table.options.meta?.refuseOrder?.(row.original.id)}>
                    <CloseRoundedIcon />
                  </ChipButton>
                </Tooltip>
              </>
            )}

            {/* Secondary Actions Menu */}
            {![OrderStatus.Pending, OrderStatus.Canceled].includes(row.original.status as OrderStatus) && (
              <PopupState variant="popover" popupId={`order-${row.id}-actions`}>
                {(popupState) => (
                  <>
                    <IconButton {...bindTrigger(popupState)} size="small">
                      <MoreHorizIcon />
                    </IconButton>
                    <Menu
                      {...bindMenu(popupState)}
                      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                      {/* Consultation Actions */}
                      {row.original.status === OrderStatus.Accepted && (
                        <>
                          {row.original.meetingPlace !== AvailabilityMeetingPlace.VISIO_CONFERENCE && (
                            <MenuItem
                              onClick={() => {
                                table.options.meta?.startLocalConsultation?.(row.original.id, row.original.patient?.id || '')
                                popupState.close()
                              }}
                            >
                              <ListItemIcon>
                                <LaunchIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>{t('page.meetingLink.ordersTable.actions.startLocalConsultation')}</ListItemText>
                            </MenuItem>
                          )}
                          <MenuItem
                            component={Link}
                            to={`/consultations/create?orderId=${row.original.id}&patient=${row.original.patient?.id || ''}&speciality=${
                              row.original.speciality
                            }&startDate=${row.original.startDate}`}
                          >
                            <ListItemIcon>
                              <VideoCallIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>{t('page.meetingLink.ordersTable.actions.scheduleConsultation')}</ListItemText>
                          </MenuItem>
                        </>
                      )}

                      {/* View Consultation */}
                      {row.original.status === OrderStatus.Consultation_Created && (
                        <MenuItem component={Link} to={`/consultations/${row.original.consultationId}`}>
                          <ListItemIcon>
                            <LaunchIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>{t('page.meetingLink.ordersTable.actions.openConsultation')}</ListItemText>
                        </MenuItem>
                      )}

                      {/* Cancel Order */}
                      <MenuItem
                        onClick={() => {
                          table.options.meta?.cancelOrder?.(row.original.id)
                          popupState.close()
                        }}
                      >
                        <ListItemIcon>
                          <CancelRoundedIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{t('page.meetingLink.ordersTable.actions.cancelOrder')}</ListItemText>
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </PopupState>
            )}

            {/* Convert Order Action */}
            <Tooltip title={t('page.meetingLink.ordersTable.actions.convert')}>
              <IconButton onClick={() => handleConvertClick(row.original.id)} size="small" color="primary">
                <CompareArrows fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
        meta: {
          align: 'center',
          sxCell: { width: '50px', position: 'sticky', right: 0, borderLeft: '1px solid #efefef', zIndex: 1, background: '#FFF', minWidth: '50px' },
          sxHeader: { width: '50px', position: 'sticky', right: 0, borderLeft: '1px solid #efefef', zIndex: 10, background: '#f9fafb', minWidth: '50px' },
        },
      }),
    ],
    [t]
  )

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case OrderStatus.Accepted:
        return 'success'
      case OrderStatus.Consultation_Created:
        return 'primary'
      case OrderStatus.Canceled:
      case OrderStatus.Refused:
        return 'error'
      case OrderStatus.Pending:
        return 'warning'
      default:
        return 'default'
    }
  }

  return (
    <>
      <Table
        columns={columns}
        data={data}
        disableGlobalSearch
        manualPagination={manualPagination}
        meta={{
          acceptOrder: (orderId) => {
            onAcceptOrder?.(orderId)
          },
          cancelOrder: (orderId) => {
            onCancelOrder?.(orderId)
          },
          refuseOrder: (orderId) => {
            onRefuseOrder?.(orderId)
          },
          startLocalConsultation: (orderId: string, patientId: string) => {
            onStartLocalConsultation?.(orderId, patientId)
          },
        }}
        onPaginationChange={onPaginationChange}
        totalRows={totalCount}
      />

      {selectedOrder && (
        <ConvertOrderDialogContainer
          open={convertDialogOpen}
          onClose={handleCloseConvertDialog}
          orderId={selectedOrder}
          meetingPlace={data.find((order) => order.id === selectedOrder)?.meetingPlace as AvailabilityMeetingPlace}
        />
      )}
    </>
  )
}

export default OrdersTable
